import { gsap } from 'gsap';
import { getRevealSlowMult, transitionLog } from '../debug/transitionDebug.js';

/**
 * Channel wipe — the one transition for the whole site.
 *
 * Concept: every screen change is the CRT "changing the channel". A fixed,
 * transparent WebGL canvas is laid over the page holding ONLY the outgoing
 * picture (card art, a snapshot of the world, the loader…). A chunky,
 * per-cell front sweeps top → bottom: cells ahead of the front still show the
 * outgoing signal (with a little scanline + sync tear as the front nears),
 * cells in the band crumble into blocky pixels and static, then drop out to
 * fully transparent.
 *
 * Because the canvas is transparent where it has revealed, the thing being
 * revealed is always the REAL next screen — live video, iframes, Phaser, all
 * of it — never a snapshot. The caller's only job is to have that screen
 * mounted and ready underneath before calling this.
 *
 * Protocol:
 *   1. Canvas is created and the first (fully opaque) frame is painted.
 *   2. After the GPU has had two frames to composite it, `onCoverReady()` is
 *      called and `coverLayer` (if given) is removed. Swap the DOM beneath
 *      the canvas here — it is invisible to the user.
 *   3. The wipe runs, the canvas is removed, the promise resolves.
 *
 * @param {Object} o
 * @param {HTMLImageElement|HTMLCanvasElement|HTMLVideoElement|null} [o.source] —
 *   outgoing picture. Videos keep playing inside the wipe (texture refreshed
 *   every frame). `null` = solid #070707.
 * @param {('cover'|'stretch')} [o.fit='cover'] — `cover` matches
 *   `background-size: cover`; `stretch` for viewport-sized snapshots.
 * @param {string} [o.imageUrl] — convenience: load (or reuse cached) image.
 * @param {HTMLElement|null} [o.coverLayer] — removed once the canvas is on screen.
 * @param {() => void} [o.onCoverReady] — swap the page underneath here.
 * @param {number} [o.zIndex=2400]
 * @param {string} [o.className='world-channel-wipe']
 * @param {number} [o.duration]
 * @returns {Promise<void>}
 */

const DURATION = 1.05;
/** Fraction of the sweep occupied by the crumbling band. */
const BAND = 0.26;
const REDUCED =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

const VS = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos;                      // (0,0) bottom-left … (1,1) top-right
  gl_Position = vec4(aPos * 2.0 - 1.0, 0.0, 1.0);
}
`;

const FS = `
precision mediump float;
uniform sampler2D uTex;
uniform float uHasTex;
uniform vec2  uUvScale;
uniform vec2  uUvOffset;
uniform float uT;          // 0 → 1
uniform float uBand;
uniform vec2  uGrid;       // cells across / down
uniform float uScatter;
uniform float uBlock;      // max chunk px
uniform vec3  uMid;
uniform vec2  uRes;        // canvas px
uniform float uTime;
varying vec2 vUv;

float h01(vec2 c) { return fract(sin(dot(c, vec2(12.9898, 78.233))) * 43758.5453); }

vec3 src(vec2 uv) {
  if (uHasTex < 0.5) return uMid;
  vec2 s = uUvOffset + clamp(uv, 0.0, 1.0) * uUvScale;
  return texture2D(uTex, vec2(s.x, 1.0 - s.y)).rgb;
}

void main() {
  vec2 cell = floor(vec2(vUv.x * uGrid.x, (1.0 - vUv.y) * uGrid.y));
  float r0 = h01(cell);
  float r1 = h01(cell * 1.07 + vec2(3.0, 11.0));
  float speckle = uScatter * ((r0 - 0.5) * 1.1 + 0.55 * (r1 - 0.5));
  // Mean front is a straight horizontal line (top = 1); speckle punches holes.
  float pry = clamp(vUv.y + speckle, 0.0, 1.0);
  float front = 1.0 - uT * (1.0 + uBand + 0.04);
  float d = pry - front;           // > 0: the front has passed this cell
  if (d > uBand) discard;          // fully revealed → transparent

  float near = 1.0 - clamp(abs(d) / uBand, 0.0, 1.0);   // 1 at the front

  // Horizontal sync tear: whole scan rows jitter as the front approaches.
  float row = floor((1.0 - vUv.y) * uRes.y / 3.0);
  float tear = (h01(vec2(row, floor(uTime * 24.0))) - 0.5) * 0.09 * near * near;
  vec2 uv = vUv + vec2(tear, 0.0);

  if (d <= 0.0) {
    // Still tuned to the outgoing signal. Scanlines + RGB split near the front.
    float split = 0.007 * near;
    vec3 c = vec3(
      src(uv + vec2(split, 0.0)).r,
      src(uv).g,
      src(uv - vec2(split, 0.0)).b
    );
    c *= 1.0 - 0.10 * (0.5 + 0.5 * sin(gl_FragCoord.y * 3.14159));
    gl_FragColor = vec4(c, 1.0);
    return;
  }

  // In the band: chunk up, go to static, drop out.
  float b = d / uBand;                           // 0 at front … 1 gone
  float cs = uBlock * b + 1.0;
  vec2 p = (floor(uv * uRes / cs) + 0.5) * cs;
  vec3 c = src(p / uRes);
  float n = h01(cell + floor(uTime * 30.0) + vec2(r0, 0.0));
  c = mix(c, vec3(n) * 0.95, b * 0.75);
  c = mix(c, uMid, b * b);
  float a = 1.0 - b * b * b;
  if (r1 < b * 1.25 - 0.25) a = 0.0;            // some cells pop early
  gl_FragColor = vec4(c * a, a);                 // premultiplied
}
`;

const _imgCache = new Map();

/** Warm the outgoing image so the wipe can start the instant it's needed. */
export function preloadChannelImage(url) {
  if (!url) return Promise.resolve(null);
  if (_imgCache.has(url)) return _imgCache.get(url);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
  _imgCache.set(url, p);
  return p;
}

function compile(gl, type, srcText) {
  const s = gl.createShader(type);
  gl.shaderSource(s, srcText);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(s) || 'shader compile failed');
  }
  return s;
}

function twoFrames() {
  return new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}

export async function runChannelWipe({
  source = null,
  imageUrl,
  coverLayer = null,
  onCoverReady,
  zIndex = 2400,
  className = 'world-channel-wipe',
  duration = DURATION,
  fit = 'cover',
} = {}) {
  if (typeof window === 'undefined') {
    onCoverReady?.();
    coverLayer?.remove?.();
    return;
  }
  if (!source && imageUrl) source = await preloadChannelImage(imageUrl);

  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const canvas = document.createElement('canvas');
  canvas.className = className;
  canvas.setAttribute('aria-hidden', 'true');
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100vw',
    height: '100vh',
    zIndex: String(zIndex),
    pointerEvents: 'none',
    display: 'block',
  });

  const cleanupCover = () => {
    try {
      onCoverReady?.();
    } catch (e) {
      console.warn('[channelWipe] onCoverReady failed:', e);
    }
    if (coverLayer?.parentNode) coverLayer.remove();
  };

  let gl;
  try {
    gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: true, preserveDrawingBuffer: true });
    if (!gl) throw new Error('no webgl');
  } catch (e) {
    // No GL: fall back to a plain cross-fade so the page still changes cleanly.
    transitionLog('channelWipe:no_webgl', { error: String(e) });
    cleanupCover();
    return;
  }

  const prog = gl.createProgram();
  gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VS));
  gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(prog) || 'link failed');
  }
  gl.useProgram(prog);

  const vbo = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'aPos');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  const U = (n) => gl.getUniformLocation(prog, n);
  const u = {
    hasTex: U('uHasTex'), uvScale: U('uUvScale'), uvOffset: U('uUvOffset'), t: U('uT'),
    band: U('uBand'), grid: U('uGrid'), scatter: U('uScatter'), block: U('uBlock'),
    mid: U('uMid'), res: U('uRes'), time: U('uTime'),
  };

  // Outgoing picture → texture. Row 0 = top (no flip); shader flips v.
  let srcAspect = 1;
  const isVideo = typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement;
  const srcW = source ? (source.videoWidth || source.naturalWidth || source.width || 0) : 0;
  const srcH = source ? (source.videoHeight || source.naturalHeight || source.height || 0) : 0;
  const hasTex = srcW > 0 && srcH > 0;
  let tex = null;
  if (hasTex) {
    tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    srcAspect = srcW / srcH;
  }
  const isSnapshot = fit === 'stretch';

  gl.uniform1f(u.hasTex, hasTex ? 1 : 0);
  gl.uniform1f(u.band, BAND);
  gl.uniform1f(u.scatter, 0.34);
  gl.uniform3f(u.mid, 0.027, 0.027, 0.027);
  gl.clearColor(0, 0, 0, 0);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.BLEND);

  const t0 = performance.now();
  const state = { p: 0 };
  let lastW = 0;
  let lastH = 0;

  const draw = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w !== lastW || h !== lastH) {
      lastW = w;
      lastH = h;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(u.res, canvas.width, canvas.height);
      const gridH = Math.max(14, Math.min(32, Math.round(14 + 12 * (h / 1000))));
      const gridW = Math.max(16, Math.min(48, Math.round((gridH * w) / h)));
      gl.uniform2f(u.grid, gridW, gridH);
      gl.uniform1f(u.block, Math.min(48, Math.max(8, Math.floor(0.08 * Math.max(w, h)))) * dpr);
      let sx = 1;
      let sy = 1;
      if (!isSnapshot && hasTex) {
        const ra = w / h;
        if (ra > srcAspect) sy = srcAspect / ra;
        else sx = ra / srcAspect;
      }
      gl.uniform2f(u.uvScale, sx, sy);
      gl.uniform2f(u.uvOffset, (1 - sx) / 2, (1 - sy) / 2);
    }
    if (isVideo && tex && !source.paused && source.readyState >= 2) {
      // Keep the outgoing channel alive while it crumbles.
      gl.bindTexture(gl.TEXTURE_2D, tex);
      try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source); } catch { /* tainted */ }
    }
    gl.uniform1f(u.t, state.p);
    gl.uniform1f(u.time, (performance.now() - t0) / 1000);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  document.body.appendChild(canvas);
  draw();
  await twoFrames(); // let the compositor show the opaque first frame
  cleanupCover();
  transitionLog('channelWipe:start', { hasTex, isSnapshot, zIndex });

  const dispose = () => {
    try { gl.getExtension('WEBGL_lose_context')?.loseContext(); } catch { /* fine */ }
    canvas.remove();
  };

  if (REDUCED) {
    await new Promise((r) => gsap.to(canvas, { opacity: 0, duration: 0.25, onComplete: r }));
    dispose();
    return;
  }

  await new Promise((resolve) => {
    const tw = gsap.to(state, {
      p: 1,
      duration: duration * getRevealSlowMult(),
      ease: 'power2.inOut',
      onUpdate: () => {
        if (!canvas.isConnected) { tw.kill(); resolve(); return; }
        draw();
      },
      onComplete: resolve,
    });
  });
  dispose();
  transitionLog('channelWipe:done', {});
}
